/**
 * Pictures belonging to an indexed document.
 *
 * They arrive from two places and neither is authoritative on its own: the
 * `images[]` array on a pipeline_source, written when a fetch pulls the bytes
 * in, and `metadata.images`, which is what a catalogue import carries. The
 * entries themselves are just as uneven — a bare URL string from one importer,
 * an object with `url` / `src` / `originalSrc` from another.
 *
 * Both the source viewer and the RAG playground need the same answer to "what
 * pictures does this document have", so the flattening lives here rather than
 * twice.
 */

export interface DocImage {
  src: string;
  alt?: string | null;
  caption?: string | null;
}

interface RawImage {
  originalSrc?: string | null;
  url?: string | null;
  src?: string | null;
  alt?: string | null;
  caption?: string | null;
}

function push(out: DocImage[], item: unknown) {
  if (typeof item === 'string') {
    if (item.trim()) out.push({ src: item.trim() });
    return;
  }
  if (!item || typeof item !== 'object') return;
  const o = item as RawImage;
  const src = o.originalSrc || o.url || o.src;
  if (src) out.push({ src, alt: o.alt, caption: o.caption });
}

/**
 * Every picture on a document, de-duplicated by URL — the same image is
 * routinely listed both on the document and in its metadata.
 *
 * `images` is the document's own array; `metadata` is the free-form object
 * carried from the importer and onto every chunk built from the document, which
 * is why a retrieved chunk can show its product's photographs with no second
 * embedding space and no extra request.
 */
export function collectDocImages(images: unknown, metadata?: Record<string, unknown> | null): DocImage[] {
  const out: DocImage[] = [];
  for (const item of Array.isArray(images) ? images : []) push(out, item);
  const fromMeta = (metadata || {}).images;
  for (const item of Array.isArray(fromMeta) ? fromMeta : []) push(out, item);

  // De-duplicate by ASSET, not by URL. A CDN hands out the same picture under
  // several render presets — `…/STN-Pin-Brazing-Unit?$landscape400$` and
  // `…?$landscape800$` are one photograph — and comparing whole URLs showed it
  // twice, side by side, on 556 of the catalogue's documents.
  const best = new Map<string, DocImage>();
  for (const img of out) {
    const key = assetKey(img.src);
    const kept = best.get(key);
    if (!kept || renderWidth(img.src) > renderWidth(kept.src)) {
      // Keep the larger rendition, but never lose a caption the smaller one had.
      best.set(key, { ...img, alt: img.alt ?? kept?.alt, caption: img.caption ?? kept?.caption });
    }
  }
  return [...best.values()];
}

/** The picture itself, with the CDN's sizing parameters stripped off. */
function assetKey(src: string): string {
  return src.split('?')[0].split('#')[0];
}

/**
 * How big a rendition asks for, read out of the query string. Scene7 spells it
 * `$landscape800$`, other CDNs use `w=800` or `width=800`; all that matters here
 * is picking the biggest of several spellings of one image, so the largest
 * number in the query is a good enough answer and 0 means "unsized".
 */
function renderWidth(src: string): number {
  const query = src.split('?')[1];
  if (!query) return 0;
  const numbers = query.match(/\d{2,5}/g);
  return numbers ? Math.max(...numbers.map(Number)) : 0;
}
